/**
 * Movimento das listas e dos leads (design system de movimento, 23/10/2026): o que só a tela sabe
 * (o que acabou de chegar, o selo que acabou de aparecer) e o que a peça precisa mostrar enquanto
 * sai. As classes ficam em styles/movimento.css; os tempos, nos tokens.
 *
 * Dado real ou nada: só destaca o que de fato entrou na lista enquanto a tela estava aberta. A
 * primeira leitura é a base, então abrir a tela de novo (ou recarregar) não destaca nada.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { tokenMs } from '../../lib/movimento';

/**
 * --permanencia-2000 e --permanencia-4000 (tokens.css): tempo de leitura, que não muda com
 * "reduzir movimento". Ficam aqui em ms porque tokenMs() só lê os tokens em ms (--duracao-*).
 */
export const PERMANENCIA_2000 = 2000;
export const PERMANENCIA_4000 = 4000;

/** Mais do que isso de uma vez é sincronização, não "a lista nova": sem destaque (vira ruído). */
const MAX_CHEGADAS = 3;

const NADA: ReadonlySet<string> = new Set();

/** "1.234" → 1234 (o sentido da rolagem; o texto mostrado continua o formatado). */
export function numeroDe(texto: string): number {
  const digitos = texto.replace(/\D/g, '');
  return digitos ? Number(digitos) : 0;
}

/**
 * Ids que chegaram agora (Destaque do que acabou de chegar, N4): entram na lista depois da
 * primeira leitura completa (`pronto`), enquanto a tela está aberta. Valem por
 * --permanencia-2000, o tempo do .ds-destaque. `chave` recomeça a base (outro módulo).
 */
export function useChegadas(
  ids: readonly string[],
  pronto: boolean,
  chave = '',
  limite = MAX_CHEGADAS,
): ReadonlySet<string> {
  const base = useRef<{ chave: string; ids: Set<string> } | null>(null);
  const [chegadas, setChegadas] = useState<ReadonlySet<string>>(NADA);

  useEffect(() => {
    if (!pronto) return;
    if (!base.current || base.current.chave !== chave) {
      base.current = { chave, ids: new Set(ids) };
      return;
    }
    const conhecidas = base.current.ids;
    const novas = ids.filter((id) => !conhecidas.has(id));
    novas.forEach((id) => conhecidas.add(id));
    if (novas.length && novas.length <= limite) setChegadas(new Set(novas));
  }, [ids, pronto, chave, limite]);

  useEffect(() => {
    if (!chegadas.size) return undefined;
    const timer = window.setTimeout(() => setChegadas(NADA), PERMANENCIA_2000);
    return () => window.clearTimeout(timer);
  }, [chegadas]);

  return chegadas;
}

/**
 * Selos que acabaram de aparecer (Selos, N4): `${id}|${selo}` de quem já estava na tela sem
 * aquele selo, para o brilho único (.ds-brilho-unico, --duracao-700). Nunca ao abrir a tela:
 * quem aparece pela primeira vez entra como base.
 */
export function useSelosNovos(selos: ReadonlyMap<string, readonly string[]>): ReadonlySet<string> {
  const vistos = useRef(new Map<string, Set<string>>());
  const [novos, setNovos] = useState<ReadonlySet<string>>(NADA);

  useEffect(() => {
    const brilham: string[] = [];
    selos.forEach((lista, id) => {
      const antes = vistos.current.get(id);
      if (antes) {
        lista.forEach((selo) => {
          if (!antes.has(selo)) brilham.push(`${id}|${selo}`);
        });
      }
      vistos.current.set(id, new Set(lista));
    });
    if (brilham.length) setNovos(new Set(brilham));
  }, [selos]);

  useEffect(() => {
    if (!novos.size) return undefined;
    // Um pouco depois do brilho: tirar a classe antes cortaria o fim.
    const timer = window.setTimeout(() => setNovos(NADA), tokenMs('--duracao-700') + 100);
    return () => window.clearTimeout(timer);
  }, [novos]);

  return novos;
}

/**
 * O último valor de quando a peça estava aberta: enquanto ela sai (.is-aberto desligado, ainda
 * montada), continua mostrando o que mostrava, em vez de "0 selecionados" ou um menu vazio.
 */
export function useUltimoAberto<T>(aberto: boolean, valor: T): T {
  const ultimo = useRef(valor);
  useLayoutEffect(() => {
    if (aberto) ultimo.current = valor;
  });
  return aberto ? valor : ultimo.current;
}
